import React, { FC, memo, useRef, useMemo, useContext } from 'react';
import clsx from 'clsx';
import Skeleton from 'react-loading-skeleton';
import { useQuery } from '@apollo/client';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { MButton, MButtonType, MButtonSize, MButtonIconPlacement, ABody, ABodySize } from '@marriott/mi-web-ui-kit';
import { headingType, size, Heading, RichText, removeParentTagRTE } from '@marriott/mi-ui-library';
import { phoenixCobrandECMMemberDetailsQuery } from '@marriott/mi-cobrand-graphql';

import { CobrandWebSmallBannerProps, EcmPaymentMethod } from './CobrandWebSmallBanner.types';
import { StyledCobrandWebSmallBanner } from './CobrandWebSmallBanner.styles';
import { replaceWhitespaceAndAddBtn } from '../../modules/utils';
import { CTAButtonType, CHASE_ISSUER_CODE, CHASE_DEFAULT_PRIORITY } from '../../modules/constants/constants';
import { CobrandImage } from '../../molecules/CobrandImage/CobrandImage';
import { CardCompareCheckbox } from '../../molecules/CardCompareCheckbox/CardCompareCheckbox';
import { PageContext } from '../../context/PageContext';

export const cobrandwebsmallbannerConfig = {
  emptyLabel: 'CobrandWebSmallBanner',
  isEmpty: () => true,
  resourceType: `/components/content/cobrandwebsmallbanner`,
};

/**
 * CobrandWebSmallBanner Component
 * Displays a card banner with image, header, description, CTA button, and optional comparison checkbox
 *
 * Performance optimizations:
 * - Memoized with React.memo
 * - useMemo for computed values
 * - Uses CardCompareCheckbox molecule component
 */
export const CobrandWebSmallBannerComp: FC<CobrandWebSmallBannerProps> = memo<CobrandWebSmallBannerProps>(
  ({
    openInNewTab = false,
    ctaType,
    ctaLink,
    ctaLinkText,
    header,
    headerTag,
    descriptionText,
    descriptionTextMobile,
    trackingProperties,
    styleclass,
    componentId,
    dynamicMedia,
    openInNewWindowAriaLabel = '',
    customDescriptionClass,
    customContainerClass,
    customImageClass,
    cardCode,
    imagePathWithBadge,
    isCardComparisonFeatureEnabled,
    enableECMVariation,
    cardEndingLabel,
    isAuthorMode,
    ecmCardPriority,
  }) => {
    // Memoized computed values to prevent unnecessary recalculations
    const hasHeader = useMemo(() => Boolean(header?.length), [header]);
    const hasDescriptionText = useMemo(() => Boolean(descriptionText?.length), [descriptionText]);
    const hasDescriptionTextMobile = useMemo(() => Boolean(descriptionTextMobile?.length), [descriptionTextMobile]);
    // Clean RTE text - memoized to avoid re-processing
    const headerRTEText = useMemo(
      () =>
        hasHeader && header && (header.includes('<') || header.includes('>')) ? removeParentTagRTE(header) : header,
      [hasHeader, header]
    );

    // Image path with fallback - memoized
    const imagePath = useMemo(
      () => imagePathWithBadge || dynamicMedia?.assetPath,
      [imagePathWithBadge, dynamicMedia?.assetPath]
    );

    // Refs for DOM access
    const buttonRef = useRef<HTMLButtonElement | HTMLAnchorElement>(null);

    // Button type computation - memoized
    const buttonType = useMemo(() => {
      if (ctaType === CTAButtonType.PRIMARY) return MButtonType.Primary;
      if (ctaType === CTAButtonType.SECONDARY) return MButtonType.Secondary;
      return MButtonType.Tertiary;
    }, [ctaType]);

    // Button ID for checkbox - memoized
    const buttonId = useMemo(() => replaceWhitespaceAndAddBtn(componentId), [componentId]);

    const pageContext = useContext(PageContext);
    const sessionData = pageContext?.sessionData?.cacheData?.data;
    const customerId = sessionData?.consumerID;
    const currentLocale = (pageContext?.currentLocale ?? 'en_US') as string;
    const accessToken = (sessionData?.accessToken ?? pageContext?.accessToken) as string | undefined;

    const apolloContext = useMemo(() => {
      const headers: Record<string, string> = { 'accept-language': currentLocale.replace('_', '-') };
      if (process.env['IS_LOCAL_DEV'] === 'true' && accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }
      return { headers };
    }, [currentLocale, accessToken]);

    const {
      data: ecmData,
      loading: ecmLoading,
      error: ecmError,
    } = useQuery(phoenixCobrandECMMemberDetailsQuery, {
      variables: { customerId },
      context: apolloContext,
      skip: !enableECMVariation || !!isAuthorMode || !customerId,
      fetchPolicy: 'no-cache',
    });

    const isECMLoading = enableECMVariation && !!customerId && !isAuthorMode && ecmLoading && !ecmError;

    const chaseCard = useMemo(() => {
      const methods: EcmPaymentMethod[] = ecmData?.customer?.wallet?.paymentMethods ?? [];
      // Follow the Harness ECM priority order so the displayed digits match the selected
      // ?ecm= template. Default to boundless (PREP) when no priority is provided.
      const priority = ecmCardPriority?.length ? ecmCardPriority : CHASE_DEFAULT_PRIORITY;
      for (const product of priority) {
        const match = methods.find(
          card => card?.cobrand?.issuer?.code === CHASE_ISSUER_CODE && card?.cobrand?.issuerProduct?.code === product
        );
        if (match) {
          return match;
        }
      }
      return undefined;
    }, [ecmData, ecmCardPriority]);

    return (
      <StyledCobrandWebSmallBanner
        data-component-name="o-cobrand-CobrandWebSmallBanner"
        data-testid="cobrand-CobrandWebSmallBanner"
        id={componentId}
        className={clsx('col-12 col-sm-8 col-md-12 px-0', styleclass)}
        $enableECMVariation={!!enableECMVariation}
      >
        <div className={clsx('card-container t-background-color', customContainerClass)}>
          {/* Card Image */}
          <div className={clsx('card-img-container', customImageClass)}>
            <CobrandImage defaultImageURL={imagePath} altText={dynamicMedia?.altText} customClass="card-img-styles" />
          </div>
          <div
            className={clsx(
              'flexible-card-content d-flex flex-grow-1 justify-content-between flex-column flex-md-row align-items-center',
              enableECMVariation && 'align-items-md-stretch'
            )}
          >
            {/* Header and Description */}
            <div className="flexible-card-content-container">
              <div className={clsx('flexible-card', enableECMVariation && 'justify-content-center')}>
                {hasHeader && (
                  <Heading
                    customClass={clsx('mb-1 mb-md-0', enableECMVariation ? 'ml-md-4' : 'ml-md-5')}
                    variation={headingType.subtitle}
                    fontSize={size.large}
                    element={headerTag}
                    dangerouslySet={headerRTEText}
                  />
                )}
                {enableECMVariation && isECMLoading && (
                  <div className="ecm-card-ending-skeleton ml-md-5">
                    <Skeleton width={120} height={14} baseColor="#ebebeb" highlightColor="#f5f5f5" />
                  </div>
                )}
                {enableECMVariation && !isECMLoading && chaseCard?.panLast4 && (
                  <ABody
                    bodyText={`${cardEndingLabel ?? ''} \u2022\u2022\u2022${chaseCard.panLast4}`}
                    size={ABodySize.S}
                    className="ecm-card-ending ml-md-4"
                  />
                )}
                {hasDescriptionText && descriptionText && (
                  <RichText
                    text={descriptionText}
                    componentId={`${componentId}-DescriptionText`}
                    customClass={clsx(
                      'd-none d-sm-block pr-md-2 t-font-alt-s ml-md-5',
                      { 'd-block': !hasDescriptionTextMobile },
                      customDescriptionClass
                    )}
                  />
                )}
                {hasDescriptionTextMobile && descriptionTextMobile && (
                  <RichText
                    text={descriptionTextMobile}
                    componentId={`${componentId}-DescriptionTextMobile`}
                    customClass="d-block d-sm-none pr-md-2 t-font-alt-s"
                  />
                )}
              </div>
            </div>
            {/* CTA and Comparison Checkbox Section — ECM variation */}
            {enableECMVariation && ctaLinkText && (
              <div className="ecm-cta-container d-flex flex-column">
                <MButton
                  ctaLink={ctaLink}
                  buttonType={buttonType}
                  size={MButtonSize.S}
                  openInANewTab={openInNewTab}
                  openInANewTabText={openInNewWindowAriaLabel || undefined}
                  iconPlacement={openInNewTab ? MButtonIconPlacement.Trailing : MButtonIconPlacement.None}
                  trackingProperties={trackingProperties}
                  className="ecm-cta-link mt-2 mb-4 mb-md-0"
                  buttonText={ctaLinkText}
                />
              </div>
            )}
            {/* CTA and Comparison Checkbox Section — default variation */}
            {!enableECMVariation && ctaLinkText && (
              <div className="d-flex justify-content-center align-items-center flex-column flex-lg-row">
                <MButton
                  ctaLink={ctaLink}
                  buttonType={buttonType}
                  size={MButtonSize.S}
                  openInANewTab={openInNewTab}
                  openInANewTabText={openInNewWindowAriaLabel || undefined}
                  iconPlacement={openInNewTab ? MButtonIconPlacement.Trailing : MButtonIconPlacement.None}
                  trackingProperties={trackingProperties}
                  rel={openInNewTab ? 'noopener noreferrer' : undefined}
                  className="flexible-click text-center mt-3 mb-4 m-lg-0"
                  buttonText={ctaLinkText}
                  ref={buttonRef as React.Ref<HTMLButtonElement>}
                />
                {isCardComparisonFeatureEnabled && buttonId && cardCode && (
                  <CardCompareCheckbox
                    cardCode={cardCode}
                    componentId={buttonId}
                    customClass={'ml-0 ml-lg-5 mb-5 mb-md-0'}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </StyledCobrandWebSmallBanner>
    );
  }
);

CobrandWebSmallBannerComp.displayName = 'CobrandWebSmallBannerComp';

export const CobrandWebSmallBanner = (props: CobrandWebSmallBannerProps) => {
  return (
    <EditableComponent config={cobrandwebsmallbannerConfig} {...props}>
      <CobrandWebSmallBannerComp {...props} />
    </EditableComponent>
  );
};
